import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { Subject } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ButtonModule } from 'primeng/button';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { InputNumberModule } from 'primeng/inputnumber';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  formatCurrencyVND,
  formatNumber as fmtNumber,
} from '@shared/utils/date-formatter.util';
import {
  ActionButton,
  FormField,
  SummarySection,
  TableSection,
  SectionButton,
} from '@shared/components/add-form/add-form.component';
import {
  AdvancedDatepickerComponent,
  DateRange,
} from '@shared/components/advanced-datepicker/advanced-datepicker.component';
import { PaginatedDropdownController } from '@shared/utils/paginated-dropdown.util';

@Component({
  selector: 'app-order-create-layout',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    SelectModule,
    MultiSelectModule,
    InputTextModule,
    TextareaModule,
    ButtonModule,
    AutoCompleteModule,
    InputNumberModule,
    DataTableComponent,
    CheckboxModule,
    RadioButtonModule,
    AppButtonComponent,
    AdvancedDatepickerComponent,
  ],
  templateUrl: './order-create-layout.component.html',
  styleUrls: [
    './order-create-layout.component.css',
    '../../styles/paginated-dropdown.css',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderCreateLayoutComponent implements OnChanges, OnDestroy {
  @Input() titleKey: string = 'o2o.create.title';
  @Input() headerActions: ActionButton[] = [];
  @Input() formFields: FormField[] = [];
  @Input() tableSections: TableSection[] = [];
  @Input() summarySection: SummarySection | null = null;
  @Input() trailingSections: SummarySection[] = [];
  @Input() loading: boolean = false;
  @Input() initialData: any = null;
  @Input() couponSuggestions: { [key: string]: any[] } = {};
  @Input() sectionButtons: SectionButton[] = [];
  @Input() hideProductSearch: boolean = false;
  @Input() isDisableCoupon: boolean = false; // Control whether coupon field is disabled
  @Input() autoExpandPackageChildren = false;

  @Output() formAction = new EventEmitter<{ action: string; data?: any }>();
  @Output() tableAction = new EventEmitter<{
    section: string;
    action: string;
    data?: any;
  }>();
  @Output() couponSearch = new EventEmitter<{ query: string; field: string }>();
  @Output() couponSelect = new EventEmitter<{ selected: any; field: string }>();
  @Output() couponUnselect = new EventEmitter<{
    removed: any;
    field: string;
  }>();
  @Output() productSearch = new EventEmitter<{ query: string }>();
  @Output() productSelect = new EventEmitter<{ product: any }>();
  @Output() sectionAction = new EventEmitter<{
    section: string;
    action: string;
    data?: any;
  }>();

  takingDeliveryWay: any = '';
  couponValues: any[] = [];
  scrollHeight: string = '65vh';
  productSearchValue: string = '';
  productSearchSuggestions: any[] = [];
  fieldErrors: { [key: string]: string | null } = {}; // Store validation errors for each field

  // Validation subjects for each field
  private fieldValidationSubjects: {
    [key: string]: Subject<{ field: FormField; value: any }>;
  } = {};
  private readonly paginatedControllers = new Map<
    string,
    PaginatedDropdownController
  >();
  private readonly optionsCache = new Map<
    string,
    Array<{ label: string; value: any; rawLabel?: string }>
  >();
  private readonly sectionFieldsCache = new Map<string, FormField[]>();
  private readonly enrichedFieldCache = new Map<string, FormField>();
  private readonly formControls = new Map<string, any>();
  private formDirty = false;

  constructor(
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formFields']) {
      this.clearFieldCaches();
      this.syncPaginatedControllers(this.formFields || []);
      this.syncFormControls(this.formFields || []);
      this.cdr.markForCheck();
    }

    if (changes['initialData'] && !changes['initialData'].firstChange) {
      // Ignore firstChange noise; only react when parent replaces the bound object.
      this.takingDeliveryWay = this.initialData?.takingDeliveryWay ?? '';
      if (this.formFields && this.initialData) {
        this.formFields.forEach((field) => {
          if (field.disabled) return;
          if (field.validators && field.validators.length > 0) {
            const value = this.initialData[field.controlName];
            this.validateField(field, value);
          }
        });
      }
    } else if (changes['initialData']?.firstChange) {
      this.takingDeliveryWay = this.initialData?.takingDeliveryWay ?? '';
    }
  }

  private clearFieldCaches(): void {
    this.sectionFieldsCache.clear();
    this.enrichedFieldCache.clear();
    this.optionsCache.clear();
  }

  /** Notify OnPush view after parent updates dynamic field options. */
  notifyFormFieldsUpdated(): void {
    this.cdr.markForCheck();
  }

  // Watch for changes in initialData.takingDeliveryWay (called from parent when delivery method changes)
  updateTakingDeliveryWay(value: any): void {
    this.takingDeliveryWay = value ?? '';
  }

  // Handle takingDeliveryWay change from dropdown
  onTakingDeliveryWayChange(value: any): void {
    this.takingDeliveryWay = value ?? '';
    // Update initialData so parent component can detect the change
    if (this.initialData) {
      this.initialData.takingDeliveryWay = value;
    }
    // Also update via form proxy
    this.form.get('takingDeliveryWay')?.setValue(value);
    // Emit form action to notify parent
    this.formAction.emit({
      action: 'takingDeliveryWayChange',
      data: { takingDeliveryWay: value },
    });
  }

  // Handle field changes that need to notify parent (e.g., provinceId, shippingUnit, shippingService)
  onFieldChange(value: any, field: FormField): void {
    // Update initialData so parent component can detect the change
    if (this.initialData) {
      this.initialData[field.controlName] = value;
    }
    // Update form value
    this.form.get(field.controlName)?.setValue(value);

    const valueStr = value?.toString() || '';
    const cleanedValue = valueStr.replaceAll(/[\s\-().]/g, '');

    if (this.shouldValidatePhone(valueStr, cleanedValue)) {
      this.validateField(field, value);
    } else {
      this.fieldErrors[field.controlName] = null;
      this.cdr.markForCheck();
    }

    // Emit form action to notify parent
    this.formAction.emit({
      action: 'formValueChange',
      data: { [field.controlName]: value },
    });
  }

  /**
   * Check if phone number has reached exact length for validation
   * - Starts with '0': validate at exactly 10 digits
   * - Starts with '+84': validate at 11 or 12 digits
   */
  private shouldValidatePhone(value: string, cleanedValue: string): boolean {
    // Check for +84 prefix (international format)
    if (value.startsWith('+84')) {
      return cleanedValue.length === 11 || cleanedValue.length === 12;
    }
    // Check for 0 prefix (local format)
    if (value.startsWith('0')) {
      return cleanedValue.length === 10;
    }
    // Other formats - don't auto-validate on input (will validate on blur)
    return false;
  }

  // Handle blur event - validate immediately when user leaves the field
  onFieldBlur(value: any, field: FormField): void {
    // Always validate on blur
    this.validateField(field, value);
  }

  // Initialize or get the validation subject for a field
  private getOrCreateValidationSubject(
    controlName: string,
  ): Subject<{ field: FormField; value: any }> {
    if (!this.fieldValidationSubjects[controlName]) {
      this.fieldValidationSubjects[controlName] = new Subject<{
        field: FormField;
        value: any;
      }>();
    }
    return this.fieldValidationSubjects[controlName];
  }

  // Trigger validation for a field (no debounce - validate immediately)
  private triggerDebouncedValidation(field: FormField, value: any): void {
    const subject = this.getOrCreateValidationSubject(field.controlName);
    subject.next({ field, value });
  }

  // Validate a field using its configured validators
  validateField(field: FormField, value: any): void {
    // Do not validate or show errors for disabled fields (e.g. marketplace order view)
    if (field.disabled) {
      this.fieldErrors[field.controlName] = null;
      this.cdr.markForCheck();
      return;
    }
    // If field is empty, clear errors (don't show required validation)
    if (value === null || value === undefined || value === '') {
      this.fieldErrors[field.controlName] = null;
      this.cdr.markForCheck();
      return;
    }

    // maxLength is enforced even for fields with no other configured validators
    if (this.exceedsMaxLength(field, value)) {
      this.fieldErrors[field.controlName] =
        field.errorMessages?.['maxlength'] || 'common.validation.maxlength';
      this.cdr.markForCheck();
      return;
    }

    // If field has no validators, clear errors
    if (!field.validators || field.validators.length === 0) {
      this.fieldErrors[field.controlName] = null;
      this.cdr.markForCheck();
      return;
    }

    // Create a mock control object for validators
    const mockControl = {
      value: value,
    };

    // Run all validators (only if value is not empty)
    let errorKey: string | null = null;
    for (const validator of field.validators) {
      const result = validator(mockControl);
      if (result) {
        // Get the first error key
        errorKey = Object.keys(result)[0];
        break;
      }
    }

    // Set error message if validation failed
    if (errorKey && field.errorMessages?.[errorKey]) {
      this.fieldErrors[field.controlName] = field.errorMessages[errorKey];
    } else if (errorKey) {
      // Use default error message if custom message not found
      this.fieldErrors[field.controlName] = 'common.validation.invalid_value';
    } else {
      this.fieldErrors[field.controlName] = null;
    }

    this.cdr.markForCheck();
  }

  getFieldErrorParams(controlName: string): Record<string, unknown> {
    const field = (this.formFields || []).find((f) => f.controlName === controlName);
    if (!field?.maxLength) return {};
    return {
      fieldName: this.translate.instant(field.label || ''),
      requiredLength: field.maxLength,
    };
  }

  // Get error message for a field (returns null for disabled fields so no error is shown)
  getFieldError(controlName: string): string | null {
    const field = (this.formFields || []).find(
      (f) => f.controlName === controlName,
    );
    if (!field || field.disabled || field.hidden) return null;

    const customError = this.fieldErrors[controlName];
    if (customError) {
      return customError;
    }

    const control = this.form.get(controlName);
    if (
      field.required &&
      control?.touched &&
      this.isRequiredValueMissing(this.getPaymentValue(controlName))
    ) {
      return field.errorMessages?.['required'] || 'common.validation.required';
    }

    if (this.exceedsMaxLength(field, this.getPaymentValue(controlName))) {
      return field.errorMessages?.['maxlength'] || 'common.validation.maxlength';
    }

    return null;
  }

  private isRequiredValueMissing(value: unknown): boolean {
    return (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0)
    );
  }

  // Input is not capped natively, so over-limit text must surface as a field error.
  private exceedsMaxLength(field: FormField, value: unknown): boolean {
    if (!field.maxLength || value === null || value === undefined || value === '') {
      return false;
    }
    return String(value).length > field.maxLength;
  }

  // Mirrors add-form.component.ts's maxlength validator shape so the error key stays 'maxlength'.
  private createMaxLengthValidator(maxLength: number) {
    return (control: { value: any }) => {
      const value = control?.value;
      if (value === null || value === undefined || value === '') return null;
      if (String(value).length > maxLength) {
        return {
          maxlength: { requiredLength: maxLength, actualLength: String(value).length },
        };
      }
      return null;
    };
  }

  // Alias for province change (for clarity)
  onProvinceChange(value: any, field: FormField): void {
    this.onFieldChange(value, field);
  }

  ngOnDestroy(): void {
    for (const controller of this.paginatedControllers.values()) {
      controller.destroy();
    }
    this.paginatedControllers.clear();
  }

  reloadPaginatedField(controlName: string): void {
    const controller = this.paginatedControllers.get(controlName);
    if (controller) {
      controller.load({ reset: true });
      this.cdr.markForCheck();
    }
  }

  fieldsBySection(sectionKey: string): FormField[] {
    const cached = this.sectionFieldsCache.get(sectionKey);
    if (cached) {
      return cached;
    }

    const fields = (this.formFields || [])
      .filter((f) => f.section === sectionKey && !f.hidden)
      .map((field) => this.enrichPaginatedField(field));
    this.sectionFieldsCache.set(sectionKey, fields);
    return fields;
  }

  private enrichPaginatedField(field: FormField): FormField {
    const controller = this.paginatedControllers.get(field.controlName);
    if (!controller) {
      return field;
    }

    const controllerProps = controller.toSearchableDropdownProps();
    const cached = this.enrichedFieldCache.get(field.controlName);
    if (cached) {
      // Do not re-assign options/callbacks every call — that rebinds p-select and freezes UI.
      cached.loading = (controllerProps.loading || field.loading) ?? false;
      cached.loadingMore = controllerProps.loadingMore;
      cached.hasMore = controllerProps.hasMore;
      return cached;
    }

    const enriched: FormField = {
      ...field,
      type: 'searchableDropdown',
      ...controllerProps,
      loading: (controllerProps.loading || field.loading) ?? false,
    };
    this.enrichedFieldCache.set(field.controlName, enriched);
    return enriched;
  }

  trackByControlName(_index: number, field: FormField): string {
    return field.controlName;
  }

  private syncPaginatedControllers(fields: FormField[]): void {
    const activeKeys = new Set<string>();

    for (const field of fields) {
      if (!field.paginatedOptions) {
        continue;
      }

      activeKeys.add(field.controlName);

      if (!this.paginatedControllers.has(field.controlName)) {
        const controller = new PaginatedDropdownController({
          ...field.paginatedOptions,
          getCurrentValue: () => this.initialData?.[field.controlName],
          onOptionsChange: () => {
            // Invalidate options cache and refresh enriched field props once per load.
            this.optionsCache.delete(field.controlName);
            const controller = this.paginatedControllers.get(field.controlName);
            const enriched = this.enrichedFieldCache.get(field.controlName);
            if (controller && enriched) {
              const props = controller.toSearchableDropdownProps();
              Object.assign(enriched, props, {
                type: 'searchableDropdown',
                loading: props.loading ?? false,
              });
            }
            this.cdr.markForCheck();
          },
        });
        this.paginatedControllers.set(field.controlName, controller);
        controller.load({ reset: true });
      }
    }

    for (const [key, controller] of this.paginatedControllers) {
      if (!activeKeys.has(key)) {
        controller.destroy();
        this.paginatedControllers.delete(key);
        this.optionsCache.delete(key);
        this.enrichedFieldCache.delete(key);
      }
    }
  }

  getOptions(
    controlName: string,
  ): Array<{ label: string; value: any; rawLabel?: string }> {
    // Always return a stable cached array. New option array identities every CD
    // cycle make PrimeNG p-select re-bind and can freeze the page.
    const cached = this.optionsCache.get(controlName);
    if (cached !== undefined) {
      return cached;
    }

    const rawField = (this.formFields || []).find(
      (f) => f.controlName === controlName,
    );
    const field = rawField ? this.enrichPaginatedField(rawField) : undefined;
    const opts = field?.options || [];

    let result: Array<{ label: string; value: any; rawLabel?: string }>;
    if (field?.translateLabel === false) {
      result = opts.map((o: any) => ({
        ...o,
        rawLabel: o.label,
        label: o.label ?? '',
      }));
    } else {
      result = opts.map((o: any) => ({
        ...o,
        rawLabel: o.label,
        label: o.label ? this.translate.instant(o.label) : '',
      }));
    }

    this.optionsCache.set(controlName, result);
    return result;
  }

  getDeliveryValue(): any {
    const opts = this.getOptions('takingDeliveryWay') || [];
    // Prefer explicit value match if config uses 'delivery'
    let delivery = opts.find((o: any) => o?.value === 'delivery');
    if (delivery) return delivery.value;
    // Check for enum values: 'SHIP_TO_ADDRESS' or 'ShipToAddress'
    delivery = opts.find(
      (o: any) =>
        o?.value === 'SHIP_TO_ADDRESS' ||
        o?.value === 'ShipToAddress' ||
        String(o?.value).toUpperCase() === 'SHIP_TO_ADDRESS',
    );
    if (delivery) return delivery.value;
    // Fallback: use untranslated key if available
    delivery = opts.find(
      (o: any) =>
        typeof o?.rawLabel === 'string' && o.rawLabel.endsWith('.delivery'),
    );
    if (delivery) return delivery.value;
    // Last resort: try translated label text includes 'delivery' keyword in any locale
    const guess = opts.find((o: any) =>
      String(o?.label).toLowerCase().includes('delivery'),
    );
    if (guess) return guess.value;
    // Default to 'delivery' to keep previous behavior
    return 'delivery';
  }

  firstTableData(): any[] {
    const sec = (this.tableSections || [])[0];
    return (sec && (sec as any).data) || [];
  }

  onActionClick(act: any) {
    const section =
      this.tableSections?.[0]?.title || 'o2o.create.table.product_information';
    // For deleteSelected action, pass the current selection
    const data =
      act?.action === 'deleteSelected'
        ? this.tableSections[0]?.selection || []
        : undefined;
    this.tableAction.emit({
      section: String(section),
      action: act?.action || '',
      data: data,
    });
  }

  onHeaderAction(action: string): void {
    if (this.loading && this.isSubmitAction(action)) {
      return;
    }
    if (this.isSubmitAction(action) && this.hasMaxLengthViolation()) {
      this.cdr.markForCheck();
      return;
    }
    this.formAction.emit({ action, data: this.initialData });
  }

  private hasMaxLengthViolation(): boolean {
    return (this.formFields || []).some(
      (f) =>
        !f.disabled &&
        !f.hidden &&
        this.exceedsMaxLength(f, this.getPaymentValue(f.controlName)),
    );
  }

  isSubmitAction(action: string): boolean {
    return action === 'save' || action === 'submit';
  }

  isHeaderActionDisabled(action: ActionButton): boolean {
    return !!(
      action.disabled ||
      (this.loading && this.isSubmitAction(action.action))
    );
  }

  isHeaderActionLoading(action: ActionButton): boolean {
    return !!(
      action.loading ||
      (this.loading && this.isSubmitAction(action.action))
    );
  }

  getSectionButtons(sectionKey: string): SectionButton[] {
    return this.sectionButtons.filter((btn) => btn.section === sectionKey);
  }

  onSectiButtonClick(btn: SectionButton): void {
    this.sectionAction.emit({
      section: btn.section,
      action: btn.action,
    });
  }

  onFindEligiblePromotions(): void {
    this.sectionAction.emit({
      section: 'o2o.create.form.sections.discount_information',
      action: 'findEligiblePromotions',
    });
  }

  tableSelections(sectionTitle: string, selection: any[]): void {
    this.tableAction.emit({
      section: sectionTitle,
      action: 'selectionChange',
      data: selection,
    });
  }

  onTableCellValueChange(event: {
    rowData: any;
    prop: string;
    value: any;
  }): void {
    // Emit the cell value change to parent component
    this.tableAction.emit({
      section: this.tableSections[0]?.title || '',
      action: 'cellValueChange',
      data: event,
    });
  }

  onTableCellInputChange(event: {
    rowData: any;
    prop: string;
    value: any;
  }): void {
    // For quantity fields, update immediately on input change
    // This provides real-time feedback while user is typing
    if (event.prop === 'quantity') {
      this.tableAction.emit({
        section: this.tableSections[0]?.title || '',
        action: 'cellValueChange',
        data: event,
      });
    }
  }

  // Helpers for payment info and summary formatting
  getPaymentValue(controlName: string): any {
    const value = this.initialData ? (this.initialData[controlName] ?? '') : '';

    // For takingDeliveryWay field, if it's displayed as text (view mode),
    // return the label instead of the enum value
    if (controlName === 'takingDeliveryWay') {
      const field = (this.formFields || []).find(
        (f) => f.controlName === controlName,
      );
      // If field is text type (converted for view mode), return the label
      if (field?.type === 'text') {
        // Try to get the label from the label property if available
        if (this.initialData?.[`${controlName}Label`]) {
          return this.initialData[`${controlName}Label`];
        }
        // Otherwise, try to find the label from options
        const opts = this.getOptions(controlName) || [];
        const option = opts.find((o: any) => o.value === value);
        if (option) {
          return option.label;
        }
      }
    }

    return value;
  }

  formatCurrency(value: any): string {
    const formatted = formatCurrencyVND(value);
    // Replace ₫ with đ for VND currency
    return formatted.replace('₫', 'đ');
  }

  formatNumber(value: any): string {
    return fmtNumber(value);
  }

  visibleInContext(field: FormField): boolean {
    // For delivery_information section, show ALL fields when section is visible
    // (section visibility is already controlled by *ngIf in template)
    // Dependencies (like communeWardId depends on provinceId) should only affect
    // field options/enabled state, not visibility
    if (field.section === 'o2o.create.form.sections.delivery_information') {
      // All delivery_information fields should be visible when delivery is selected
      return true;
    }

    // For buyer_recipient_information section, show ALL fields always
    // Dependencies (like communeWardId depends on provinceId) should only affect
    // field options/enabled state, not visibility
    if (
      field.section === 'o2o.create.form.sections.buyer_recipient_information'
    ) {
      // All buyer_recipient_information fields should always be visible
      return true;
    }

    // For other sections, handle dependencies normally
    if (!field.dependsOn) {
      return true;
    }

    if (field.dependsOn === 'takingDeliveryWay') {
      return this.takingDeliveryWay === this.getDeliveryValue();
    }

    // Handle other dependencies
    if (field.dependsOn && this.initialData) {
      const dependentValue = this.initialData[field.dependsOn];
      return (
        dependentValue !== null &&
        dependentValue !== undefined &&
        dependentValue !== ''
      );
    }

    return true;
  }

  // Ensure numeric inputs always receive a number type
  getMinValue(field: any): number {
    return typeof field?.min === 'number' ? field.min : 0;
  }

  getMaxValue(field: any): number {
    return typeof field?.max === 'number' ? field.max : Number.MAX_SAFE_INTEGER;
  }

  getMinDate(field: FormField): Date | null {
    return field.minDate || null;
  }

  onDateSelect(fieldName: string, selectedDate: Date | DateRange | null): void {
    if (this.getPaymentValue(fieldName) === selectedDate) {
      return;
    }
    // Single write path — avoids ngModelChange + dateChange double setValue loops.
    this.form.get(fieldName)?.setValue(selectedDate);
  }

  /** Avoid re-emitting when radio re-inits with the same value during CD. */
  onRadioModelChange(controlName: string, value: any): void {
    if (this.getPaymentValue(controlName) === value) {
      return;
    }
    this.form.get(controlName)?.setValue(value);
  }

  // Backward-compatibility layer for legacy component code expecting a FormGroup API.
  // This proxy mutates initialData so the template reflects the changes.
  get form(): any {
    this.syncFormControls(this.formFields || []);
    const component = this;
    return {
      get value() {
        return { ...(component.initialData || {}) };
      },
      getRawValue: () => ({ ...(this.initialData || {}) }),
      patchValue: (obj: any, opts?: { emitEvent?: boolean }) => {
        this.patchFormValues(obj, opts);
      },
      reset: (obj?: any) => {
        const resetValues =
          obj && typeof obj === 'object'
            ? obj
            : Object.fromEntries(
                (this.formFields || []).map((field) => [
                  field.controlName,
                  field.defaultValue ?? null,
                ]),
              );
        this.initialData = { ...resetValues };
        this.formDirty = false;
        for (const control of this.formControls.values()) {
          control.markAsPristine();
          control.markAsUntouched();
          control.updateValueAndValidity({ emitEvent: false });
        }
        this.cdr.markForCheck();
      },
      markAsPristine: () => {
        this.formDirty = false;
        for (const control of this.formControls.values()) {
          control.markAsPristine();
        }
      },
      get dirty() {
        return component.formDirty;
      },
      get invalid() {
        return Array.from(component.formControls.values()).some(
          (control) => control.invalid,
        );
      },
      get controls() {
        return Object.fromEntries(component.formControls);
      },
      get: (name: string) => this.ensureFormControl(name),
    };
  }

  private syncFormControls(fields: FormField[]): void {
    for (const field of fields) {
      const control = this.ensureFormControl(field.controlName);
      control.setConfig(field);
    }
  }

  private patchFormValues(obj: any, opts?: { emitEvent?: boolean }): void {
    if (!obj || typeof obj !== 'object') {
      return;
    }

    for (const [name, value] of Object.entries(obj)) {
      this.ensureFormControl(name).setValue(value, opts);
    }
  }

  private ensureFormControl(name: string): any {
    const existing = this.formControls.get(name);
    if (existing) {
      return existing;
    }

    const valueChanges = new Subject<any>();
    let disabled = !!(this.formFields || []).find(
      (field) => field.controlName === name,
    )?.disabled;
    let validators: any[] = [];
    let errors: any = null;
    let touched = false;
    let pristine = true;
    const component = this;

    const recalculateErrors = () => {
      errors = null;
      if (disabled) {
        return;
      }

      const value = component.initialData?.[name];
      for (const validator of validators) {
        const result = validator({ value });
        if (result) {
          errors = { ...(errors || {}), ...result };
        }
      }
    };

    const writeValue = (val: any, opts?: { emitEvent?: boolean }) => {
      // Mutate in place — replacing initialData breaks the parent [initialData] binding
      // and can re-enter ngOnChanges / CD until the UI freezes.
      if (!component.initialData || typeof component.initialData !== 'object') {
        component.initialData = {};
      }
      component.initialData[name] = val;
      recalculateErrors();
      if (opts?.emitEvent !== false) {
        pristine = false;
        component.formDirty = true;
        valueChanges.next(val);
        component.formAction.emit({
          action: 'formValueChange',
          data: { [name]: val },
        });
      }
      component.cdr.markForCheck();
    };

    const control = {
      valueChanges,
      setConfig: (field: FormField) => {
        disabled = !!field.disabled;
        const configuredValidators = field.validators || [];
        validators = field.maxLength
          ? [...configuredValidators, component.createMaxLengthValidator(field.maxLength)]
          : configuredValidators;
        recalculateErrors();
      },
      get value() {
        return component.initialData?.[name];
      },
      get disabled() {
        return disabled;
      },
      get enabled() {
        return !disabled;
      },
      get invalid() {
        recalculateErrors();
        return !!errors;
      },
      get valid() {
        return !control.invalid;
      },
      get errors() {
        recalculateErrors();
        return errors;
      },
      get touched() {
        return touched;
      },
      get pristine() {
        return pristine;
      },
      get dirty() {
        return !pristine;
      },
      setValue: writeValue,
      patchValue: writeValue,
      reset: (val?: any, opts?: { emitEvent?: boolean }) => {
        const nextValue = val === undefined ? null : val;
        component.initialData = {
          ...(component.initialData || {}),
          [name]: nextValue,
        };
        touched = false;
        pristine = true;
        recalculateErrors();
        if (opts?.emitEvent !== false) {
          valueChanges.next(nextValue);
          component.formAction.emit({
            action: 'formValueChange',
            data: { [name]: nextValue },
          });
        }
        component.cdr.markForCheck();
      },
      disable: (_opts?: { emitEvent?: boolean }) => {
        disabled = true;
        recalculateErrors();
      },
      enable: (_opts?: { emitEvent?: boolean }) => {
        disabled = false;
        recalculateErrors();
      },
      setValidators: (nextValidators: any) => {
        validators = Array.isArray(nextValidators)
          ? nextValidators
          : nextValidators
            ? [nextValidators]
            : [];
        recalculateErrors();
      },
      clearValidators: () => {
        validators = [];
        recalculateErrors();
      },
      updateValueAndValidity: (_opts?: { emitEvent?: boolean }) => {
        recalculateErrors();
      },
      markAsPristine: () => {
        pristine = true;
        component.cdr.markForCheck();
      },
      markAsUntouched: () => {
        touched = false;
        component.cdr.markForCheck();
      },
      markAsTouched: () => {
        touched = true;
        component.cdr.markForCheck();
      },
      setErrors: (nextErrors: any) => {
        errors = nextErrors;
        component.cdr.markForCheck();
      },
    };

    this.formControls.set(name, control);
    return control;
  }

  // Coupon autocomplete methods
  updateCouponSuggestions(fieldName: string, suggestions: any[]): void {
    // Ensure a new object reference so p-autocomplete detects changes
    this.couponSuggestions = {
      ...this.couponSuggestions,
      [fieldName]: [...(suggestions || [])],
    };
    this.cdr.markForCheck();
  }

  clearCouponSuggestions(fieldName: string): void {
    this.couponSuggestions = {
      ...this.couponSuggestions,
      [fieldName]: [],
    };
    this.cdr.markForCheck();
  }

  // Product search methods
  onProductSearchField(event: any): void {
    const query = (event?.query ?? '').toString().trim();
    if (query.length >= 1) {
      this.productSearch.emit({ query });
      // Set panel width after search triggers
      this.setAutocompletePanelWidth();
    } else {
      this.productSearchSuggestions = [];
    }
  }

  private setAutocompletePanelWidth(): void {
    // PrimeNG renders panel in body, so we need to:
    // 1. Calculate input width
    // 2. Apply it to the panel when it appears
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      setTimeout(() => {
        const wrapper = document.querySelector(
          '.product-search-wrapper',
        ) as HTMLElement;
        const input = wrapper?.querySelector(
          '.p-autocomplete-input',
        ) as HTMLElement;

        if (input && wrapper) {
          const inputWidth = input.offsetWidth;
          // Set CSS variable on document root for global access
          document.documentElement.style.setProperty(
            '--product-search-input-width',
            `${inputWidth}px`,
          );

          // Also mark the panel with data attribute when it appears
          const panel = document.querySelector(
            '.p-autocomplete-panel',
          ) as HTMLElement;
          if (panel?.querySelector('.product-search-item')) {
            panel.dataset['productSearch'] = 'true';
            panel.style.width = `${inputWidth}px`;
            panel.style.minWidth = `${inputWidth}px`;
            panel.style.maxWidth = `${inputWidth}px`;
          }
        }
      }, 100); // Small delay to ensure panel is rendered
    });
  }

  onProductSearchSelect(event: any): void {
    if (event) {
      this.productSelect.emit({ product: event });
      this.productSearchValue = '';
      this.productSearchSuggestions = [];
    }
  }

  updateProductSearchSuggestions(suggestions: any[]): void {
    this.productSearchSuggestions = [...(suggestions || [])];
    this.cdr.markForCheck();
    // Set panel width after suggestions are updated and panel is rendered
    if (suggestions && suggestions.length > 0) {
      // Use multiple timeouts to catch panel when it appears
      setTimeout(() => this.setAutocompletePanelWidth(), 50);
      setTimeout(() => this.setAutocompletePanelWidth(), 150);
    }
  }
}
